import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { scrollTargetIntoRoot, useAppScrollRoot } from '~/components/Layout/appScrollRoot';
import { fragmentId } from '~/routing/ScrollToTop';
import { FAQ } from './FAQ';

/**
 * The knowledge base inside the signed-in app, behind the top bar's ? button. [MCV-1724]
 *
 * Same content and navigation as the public `/faq`, rendered in the `app` layout (see
 * faqLayout.ts) so it sits inside Layout's scrolling column instead of under a fixed site header.
 *
 * `ScrollToTop` is not used here: it serves the public shells and scrolls the window, and in the
 * app the window never scrolls. This does its job for Layout's column instead:
 * - A hash scrolls its section into view, inside the column only (`scrollTargetIntoRoot`, which
 *   honours the section's scroll-margin-top, so the landing offset and the scroll-spy's activation
 *   line agree). `scrollIntoView` is not used: it would also scroll the document, which must
 *   never move in the app shell.
 * - No hash resets the column to the top. VaultLayout and HlxLayout each render ONE Layout that
 *   stays mounted across route changes, so without this the FAQ would open at whatever offset the
 *   previous page was scrolled to.
 * - Leaving the FAQ resets the column too, for the same reason in the other direction: the next page
 *   would otherwise open at the offset the (much longer) FAQ was left at.
 *
 * The effect keys on the location key, not only the hash. Pressing the ? while already on the FAQ
 * navigates to the bare URL with a new key: a replace when the router is already at the bare path
 * (tree clicks only replaceState the real URL, which the router never sees), or a push when the user
 * arrived by a deep link. Either way the column goes back to the top.
 */
export const InAppFaq = () => {
  const { hash, key } = useLocation();
  const scrollRoot = useAppScrollRoot();

  useEffect(() => {
    const id = fragmentId(hash);
    const target = id ? document.getElementById(id) : null;
    const root = scrollRoot?.current;
    if (target) {
      if (root) scrollTargetIntoRoot(root, target);
      // Outside Layout there is no column to scroll. jsdom has no scrollIntoView.
      else if (typeof target.scrollIntoView === 'function')
        target.scrollIntoView({ block: 'start' });
      return;
    }
    if (root) root.scrollTop = 0;
  }, [hash, key, scrollRoot]);

  // Only on unmount: a reset in the effect above's cleanup would also run on every hash change.
  // When the whole Layout goes (sign-out) the ref is already cleared and there is nothing to reset.
  useEffect(
    () => () => {
      if (scrollRoot?.current) scrollRoot.current.scrollTop = 0;
    },
    [scrollRoot]
  );

  return <FAQ layout="app" />;
};
